import { useState, useEffect } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import './KeyFields.css';

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function appliesBadgeClass(appliesTo) {
  if (appliesTo === 'Both') return 'badge-approved';
  if (appliesTo === 'MSA') return 'badge-draft';
  return 'badge-pending';
}

export default function KeyFields() {
  const { user } = useAuth();
  const [tab, setTab] = useState('MSA');
  const [fields, setFields] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([api.get('/key-fields'), api.get('/settings/key-field-groups')])
      .then(([f, g]) => {
        if (!mounted) return;
        setFields(f || []);
        setGroups(g || []);
      })
      .catch(() => {
        if (mounted) {
          setFields([]);
          setGroups([]);
        }
      })
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const refreshFields = () => api.get('/key-fields').then(setFields);

  const filtered = fields.filter(f => {
    if (f.applies_to !== tab && f.applies_to !== 'Both') return false;
    if (search) {
      const s = search.toLowerCase();
      return `${f.field_name} ${f.field_label} ${f.field_group} ${f.description || ''}`.toLowerCase().includes(s);
    }
    return true;
  });

  const grouped = filtered.reduce((acc, f) => {
    (acc[f.field_group] = acc[f.field_group] || []).push(f);
    return acc;
  }, {});

  const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const hasFilters = !!search;

  const saveField = async () => {
    try {
      if (modal.id) await api.put(`/key-fields/${modal.id}`, modal);
      else await api.post('/key-fields', modal);
      setModal(null);
      await refreshFields();
    } catch (e) {
      alert(e.message);
    }
  };

  const highlightMatch = text => {
    if (!search || !text) return text;
    const idx = text.toLowerCase().indexOf(search.toLowerCase());
    if (idx < 0) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="clause-lib-mark">{text.slice(idx, idx + search.length)}</mark>
        {text.slice(idx + search.length)}
      </>
    );
  };

  return (
    <>
      <div className="page-header">
        <h1>Key Fields</h1>
      </div>
      <div className="page-content clause-library-page">
        <div className="clause-lib-tab-strip" role="tablist" aria-label="Key fields agreement type">
          <button
            type="button"
            role="tab"
            id="kf-tab-msa"
            aria-selected={tab === 'MSA'}
            aria-controls="key-fields-panel"
            className={`clause-lib-tab ${tab === 'MSA' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('MSA')}
          >
            MSA Key Fields
          </button>
          <button
            type="button"
            role="tab"
            id="kf-tab-sow"
            aria-selected={tab === 'SOW'}
            aria-controls="key-fields-panel"
            className={`clause-lib-tab ${tab === 'SOW' ? 'clause-lib-tab-active' : ''}`}
            onClick={() => setTab('SOW')}
          >
            SOW Key Fields
          </button>
        </div>

        <div
          id="key-fields-panel"
          role="tabpanel"
          aria-labelledby={tab === 'MSA' ? 'kf-tab-msa' : 'kf-tab-sow'}
        >
          <div className="key-fields-stats-row">
            <div className="clause-lib-stats" aria-label="Filtered key field counts">
              <div className="clause-lib-stat-card" title="Definitions matching current tab and search">
                <div className="clause-lib-stat-card-label">In view</div>
                <div className="clause-lib-stat-card-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
              </div>
              <div className="clause-lib-stat-card" title="Distinct groups in filtered results">
                <div className="clause-lib-stat-card-label">Groups</div>
                <div className="clause-lib-stat-card-value">{loading ? '—' : groupKeys.length.toLocaleString()}</div>
              </div>
            </div>
            {user?.role === 'Admin' && (
              <button
                type="button"
                className="btn btn-primary btn-sm key-fields-new-btn"
                onClick={() =>
                  setModal({
                    field_name: '',
                    field_label: '',
                    field_type: 'text',
                    field_group: '',
                    description: '',
                    applies_to: tab,
                    is_active: 1,
                  })
                }
              >
                + Add Key Field
              </button>
            )}
          </div>

          <div className="clause-lib-toolbar">
            <div className="clause-lib-search-wrap">
              <SearchIcon />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search variable name, label, group, description…"
                aria-label="Search key fields"
              />
            </div>
            <div className="clause-lib-toolbar-meta">
              {hasFilters && (
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setSearch('')}>
                  Clear search
                </button>
              )}
            </div>
          </div>

          {loading && (
            <div className="empty-state clause-lib-empty" role="status">
              <p>Loading key fields…</p>
            </div>
          )}

          {!loading &&
            groupKeys.map(group => {
              const gFields = grouped[group];
              return (
                <div className="card clause-lib-group" key={group}>
                  <div className="card-header">
                    <div className="clause-lib-group-title-row">
                      <span className="clause-lib-group-title">{highlightMatch(group)}</span>
                      <span className="clause-lib-code-pill">
                        {gFields.length} field{gFields.length === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Variable name</th>
                          <th>Label</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Applies to</th>
                          <th>Active</th>
                          {user?.role === 'Admin' && <th className="text-right clause-lib-actions-th">Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {gFields.map(f => (
                          <tr key={f.id}>
                            <td className="key-fields-var">{highlightMatch(f.field_name)}</td>
                            <td>{highlightMatch(f.field_label)}</td>
                            <td>
                              <span className="badge badge-draft">{f.field_type}</span>
                            </td>
                            <td className="key-fields-desc">{highlightMatch(f.description || '')}</td>
                            <td>
                              <span className={`badge ${appliesBadgeClass(f.applies_to)}`}>{f.applies_to}</span>
                            </td>
                            <td>
                              {f.is_active ? (
                                <span className="badge badge-approved">Yes</span>
                              ) : (
                                <span className="badge badge-expired">No</span>
                              )}
                            </td>
                            {user?.role === 'Admin' && (
                              <td className="clause-lib-actions-cell text-right">
                                <div className="clause-lib-row-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline clause-lib-icon-btn"
                                    title="Edit key field"
                                    aria-label={`Edit ${f.field_name}`}
                                    onClick={() => setModal({ ...f })}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                      <path d="M12 20h9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                      <path
                                        d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}

          {!loading && groupKeys.length === 0 && (
            <div className="empty-state clause-lib-empty">
              <h3>No key fields match your filters</h3>
              <p>
                {search.trim()
                  ? 'Try clearing search or switching MSA / SOW.'
                  : `No ${tab} key field definitions are loaded for this tab yet.`}
              </p>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{modal.id ? 'Edit' : 'Add'} Key Field</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Variable name</label>
                <input
                  value={modal.field_name}
                  onChange={e => setModal(f => ({ ...f, field_name: e.target.value }))}
                  placeholder="e.g. payment_terms"
                />
              </div>
              <div className="form-group">
                <label>Label</label>
                <input value={modal.field_label} onChange={e => setModal(f => ({ ...f, field_label: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={modal.field_type} onChange={e => setModal(f => ({ ...f, field_type: e.target.value }))}>
                  <option value="text">text</option>
                  <option value="number">number</option>
                  <option value="date">date</option>
                  <option value="textarea">textarea</option>
                </select>
              </div>
              <div className="form-group">
                <label>Group</label>
                <select value={modal.field_group} onChange={e => setModal(f => ({ ...f, field_group: e.target.value }))}>
                  <option value="">Select group</option>
                  {groups
                    .filter(
                      g =>
                        g.is_active &&
                        (g.applies_to === modal.applies_to ||
                          g.applies_to === 'Both' ||
                          modal.applies_to === 'Both')
                    )
                    .map(g => (
                      <option key={g.id} value={g.group_name}>
                        {g.group_name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label>Applies to</label>
                <select value={modal.applies_to} onChange={e => setModal(f => ({ ...f, applies_to: e.target.value }))}>
                  <option value="MSA">MSA</option>
                  <option value="SOW">SOW</option>
                  <option value="Both">Both</option>
                </select>
              </div>
              <div className="form-group full-width">
                <label>Description</label>
                <textarea value={modal.description || ''} onChange={e => setModal(f => ({ ...f, description: e.target.value }))} />
              </div>
              {modal.id && (
                <div className="form-group">
                  <label>Active</label>
                  <select
                    value={modal.is_active}
                    onChange={e => setModal(f => ({ ...f, is_active: parseInt(e.target.value, 10) }))}
                  >
                    <option value={1}>Yes</option>
                    <option value={0}>No</option>
                  </select>
                </div>
              )}
            </div>
            <div className="btn-group mt-4">
              <button type="button" className="btn btn-primary" onClick={saveField}>
                Save
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
